import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import {
  InternalReportStatusEnum,
  ReportStatus,
} from "../api/models/report.models";
import {
  adminChangeReportInternalStatus,
  adminChangeReportStatus,
} from "../api/report.api";

const useAdminReportCommands = () => {
  const { t } = useTranslation();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef]),
  );

  const queryClient = useQueryClient();

  const updateReportStatus = async (payload: {
    id: number;
    status: ReportStatus;
  }) => {
    const result = await updateReportStatusMutation.mutateAsync(payload);
    return result;
  };

  const updateReportStatusMutation = useMutation({
    mutationFn: (payload: { id: number; status: ReportStatus }) =>
      adminChangeReportStatus(
        payload.id,
        payload.status,
        bearerToken as string,
      ),
    onError: (error) => {
      console.error("Failed to update report status:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-report-status-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-report-status-updated"),
        life: 3000,
      });

      queryClient.invalidateQueries({ queryKey: ["adminReports"] });
      queryClient.invalidateQueries({ queryKey: ["adminAllReports"] });
    },
  });

  const updateInternalStatus = async (payload: {
    id: number;
    internalStatus: InternalReportStatusEnum;
  }) => {
    const result =
      await updateInternalReportStatusMutation.mutateAsync(payload);
    return result;
  };

  const updateInternalReportStatusMutation = useMutation({
    mutationFn: (payload: {
      id: number;
      internalStatus: InternalReportStatusEnum;
    }) =>
      adminChangeReportInternalStatus(
        payload.id,
        payload.internalStatus,
        bearerToken as string,
      ),
    onError: (error) => {
      console.error("Failed to update internal report status:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-internal-report-status-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-internal-report-status-updated"),
        life: 3000,
      });

      queryClient.invalidateQueries({ queryKey: ["adminReports"] });
      queryClient.invalidateQueries({ queryKey: ["adminAllReports"] });
    },
  });

  const isUpdatingReportStatus = updateReportStatusMutation.isPending;
  const isUpdatingInternalReportStatus =
    updateInternalReportStatusMutation.isPending;

  return {
    updateReportStatus,
    isUpdatingReportStatus,
    updateInternalStatus,
    isUpdatingInternalReportStatus,
  };
};

export default useAdminReportCommands;
